import type { AgentDto, ChannelDto, ChannelPostDto, RunDto, RunEventDto, TaskDto } from '@af/shared';
import { outputPlain, type ToolActivityKind, toolActivityKind } from './tool-display';

export type ActivityFilter = 'all' | ToolActivityKind;
export interface CompanyActivityItem {
  id: string;
  kind: ToolActivityKind;
  from: string | null;
  to: string | null;
  text: string;
  action: string | null;
  createdAt: string;
  status: string | null;
  channelId?: string;
  thread?: string;
  runId?: string;
  taskId?: string;
  /** Linked source evidence, including calls folded into their visible post. */
  details: unknown[];
  attempt?: boolean;
  failedAction?: boolean;
}

const object = (v: unknown): Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
const str = (v: unknown) => (typeof v === 'string' && v ? v : null);
function resultObject(output: unknown) {
  try {
    return object(JSON.parse(outputPlain(output)));
  } catch {
    return {};
  }
}

/** Build one presentation row per post/call. No communication is executed here. */
export function companyActivity({
  agents,
  channels,
  posts,
  tasks,
  runs,
}: {
  agents: AgentDto[];
  channels: ChannelDto[];
  posts: ChannelPostDto[];
  tasks: TaskDto[];
  runs: { run: RunDto; events: RunEventDto[] }[];
}): CompanyActivityItem[] {
  const names = new Map(agents.map((a) => [a.id, a.name]));
  const resolveAgent = (v: unknown) => {
    const ref = str(v);
    if (!ref) return null;
    return agents.find((a) => a.id === ref || a.slug === ref || a.name === ref)?.name ?? ref;
  };
  const byChannel = new Map(channels.map((c) => [c.id, c]));
  const byTask = new Map(tasks.map((t) => [t.id, t]));
  const sourceByRow = new Map(posts.map((p) => [`post:${p.id}`, p]));
  const items: CompanyActivityItem[] = [];
  const postRows = new Map<string, CompanyActivityItem>();
  for (const p of posts) {
    // Human chat is already available in the channel itself. Unknown authors stay explicit.
    if (p.kind === 'message' && p.authorUserId) continue;
    const m = p.meta ?? {};
    const taskEvent = p.kind === 'system' && (m.event === 'task_delegated' || m.event === 'task_reported');
    const taskId = str(m.taskId);
    const task = taskId ? byTask.get(taskId) : null;
    const c = byChannel.get(p.channelId);
    const fromId = str(m.agentId) ?? p.authorAgentId;
    const toId =
      str(m.toAgentId) ?? (c?.kind === 'dm' ? c.members.find((a) => a.agentId !== fromId)?.agentId : null);
    const row: CompanyActivityItem = {
      id: `post:${p.id}`,
      kind: taskEvent ? 'tasks' : p.kind === 'message' ? 'messages' : 'system',
      from: (fromId ? names.get(fromId) : null) ?? (p.authorName || null),
      to: c?.kind === 'channel' ? `#${c.slug}` : resolveAgent(toId),
      text: p.body,
      action: p.kind === 'system' ? str(m.event) : null,
      createdAt: p.createdAt,
      status: task?.status ?? str(m.status) ?? (p.kind === 'system' ? 'recorded' : 'posted'),
      channelId: p.channelId,
      thread: p.replyToId ?? p.id,
      runId: task?.runId ?? str(m.runId) ?? p.runId ?? undefined,
      taskId: taskId ?? undefined,
      details: [p],
    };
    items.push(row);
    postRows.set(p.id, row);
  }
  for (const { run, events } of runs) {
    // Fold results into their call, instead of displaying them as new conversation lines.
    const results = new Map(
      events.filter((e) => e.type === 'tool-result').map((e) => [str(e.payload.toolCallId), e]),
    );
    for (const e of events) {
      if (e.type !== 'tool-call') continue;
      const p = e.payload;
      const name = str(p.toolName) ?? '';
      const kind = toolActivityKind(name);
      const input = object(p.input);
      const callId = str(p.toolCallId);
      const result = callId ? results.get(callId) : undefined;
      const output = resultObject(result?.payload.output);
      const taskId = str(output.taskId);
      const channelRef = str(input.channel);
      const targetRef = str(input.agent) ?? str(input.role);
      const body = str(input.message) ?? str(input.text);
      // Prefer stable IDs. DM mirrors can have another ID: correlate exact run, author,
      // body and destination metadata, never merely similar text or a nearby timestamp.
      const direct =
        kind === 'system' ? undefined : postRows.get(str(output.postId) ?? str(output.messageId) ?? '');
      const matching =
        direct ??
        (kind === 'messages'
          ? items.find((item) => {
              const source = sourceByRow.get(item.id);
              const c = source ? byChannel.get(source.channelId) : null;
              if (
                source?.kind !== 'message' ||
                source.runId !== run.id ||
                source.authorAgentId !== run.agentId ||
                !body ||
                source.body !== body
              )
                return false;
              if (name === 'channel_post')
                return (
                  channelRef === c?.slug ||
                  channelRef === `#${c?.slug}` ||
                  (channelRef?.startsWith('@') &&
                    c?.kind === 'dm' &&
                    c.members.some(
                      (m) =>
                        m.agentId !== run.agentId &&
                        resolveAgent(m.agentId) === resolveAgent(channelRef.slice(1)),
                    ))
                );
              return (
                c?.kind === 'dm' &&
                c.members.some(
                  (m) => m.agentId !== run.agentId && resolveAgent(m.agentId) === resolveAgent(targetRef),
                )
              );
            })
          : taskId
            ? items.find(
                (item) => item.kind === 'tasks' && item.taskId === taskId && item.action === 'task_delegated',
              )
            : undefined);
      if (matching) {
        if (result?.payload.isError) matching.failedAction = true;
        matching.details.push({ runId: run.id, call: e, result });
        continue;
      }
      const task = taskId ? byTask.get(taskId) : null;
      const targetRun = str(output.runId);
      items.push({
        id: `call:${run.id}:${e.seq}`,
        kind,
        from: names.get(run.agentId) ?? run.agentId,
        to: resolveAgent(targetRef) ?? channelRef,
        text:
          kind === 'tasks'
            ? (str(input.title) ?? str(input.task) ?? '')
            : kind === 'messages'
              ? (body ?? '')
              : '',
        action: name || null,
        createdAt: e.createdAt,
        status: result?.payload.isError
          ? 'failed'
          : kind === 'tasks'
            ? (task?.status ?? str(output.status) ?? 'unconfirmed')
            : result
              ? 'completed'
              : 'unconfirmed',
        runId: task?.runId ?? targetRun ?? run.id,
        taskId: taskId ?? undefined,
        details: [{ runId: run.id, call: e, result }],
        attempt: kind === 'messages',
      });
    }
  }
  return items.sort((a, b) => b.createdAt.localeCompare(a.createdAt) || a.id.localeCompare(b.id));
}

export function filterCompanyActivity(items: CompanyActivityItem[], filter: ActivityFilter) {
  return filter === 'all' ? items : items.filter((item) => item.kind === filter);
}
